'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepForm } from '../../../../form-controls/MultistepForm/MultistepForm';
import { toast } from '../../../../Toast/Toast';
import { MyAccountSheet } from '../../../MyAccountSheet/MyAccountSheet';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { SetupStep } from './steps/SetupStep';
import { getInitialRealityCheckInterval } from './utils/realityCheckIntervals';

type RealityCheckSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentInterval?: string;
  trigger?: React.ReactNode;
};

const RealityCheckSheet = ({ open, onOpenChange, currentInterval, trigger }: RealityCheckSheetProps) => {
  const t = useTranslations('my_account.safer_gambling.reality_check');
  const queryClient = useQueryClient();
  const debug = createDebug({ isEnabled: false });

  // Define the form schema
  const formSchema = z.object({
    interval: z.string().min(1, 'Please select a reminder interval'),
  });

  type FormData = z.infer<typeof formSchema>;

  // Initialize the form as controlled form
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    values: {
      interval: getInitialRealityCheckInterval(currentInterval),
    },
  });

  // Create mutation for reality check API
  const realityCheckMutation = useApiMutation<{ success: boolean; message: string; value: string }, { value: string }>(API_ROUTES.MY_ACCOUNT.REALITY_CHECK.POST, 'POST', {
    mutationOptions: {
      onSuccess: data => {
        debug.log('Reality check update successful:', data);
        // Invalidate the reality check query to refresh the data
        queryClient.invalidateQueries({ queryKey: ['api', API_ROUTES.MY_ACCOUNT.REALITY_CHECK.GET] });
      },
      onError: error => {
        debug.error('Reality check update failed:', error);
        toast(t('steps.setup.error_message'), { type: 'error' });
      },
    },
  });

  // Define steps
  const steps = [
    {
      id: 'setup',
      component: <SetupStep mutation={realityCheckMutation} />,
      schema: formSchema.pick({ interval: true }),
    },
    {
      id: 'confirmation',
      component: <ConfirmationStep />,
      schema: z.object({}),
    },
  ];

  const handleClose = () => onOpenChange(false);

  return (
    <MyAccountSheet trigger={trigger} title={t('title')} open={open} onOpenChange={onOpenChange}>
      <FormProvider {...form}>
        <MultistepForm steps={steps} showProgressBar={false} showNavigation={false} onComplete={handleClose} className="h-full" />
      </FormProvider>
    </MyAccountSheet>
  );
};

export { RealityCheckSheet };
